import * as Sentry from '@sentry/react';

import { getClient, getEnv, detectWebGLSupport, isEmbedded } from 'utils/utils';
import beforeBreadcrumb from './breadcrumbs';

const env = getEnv();
const isProduction = env === 'production';
const client = getClient();
const BROWSER_SETTINGS = 'browserSettings';

const errorCodeIgnoreList = ['null_origin', 'content_moderation_error'];

function addTranslationDataToEventContext(event) {
  const documentLanguage = document.documentElement.lang;

  event.contexts[BROWSER_SETTINGS].documentLanguage = documentLanguage;
  event.contexts[BROWSER_SETTINGS].preferredLanguage = navigator.language;
  event.contexts[BROWSER_SETTINGS].isTranslated = documentLanguage !== 'en-us'; // en-us is the value we set in index.html as the lang tag, changes to it would come from a third party translator
}

export function beforeSend(event, hint) {
  try {
    const { originalException } = hint;

    // filter out specific error codes
    if (errorCodeIgnoreList.includes(originalException.response?.data?.code)) return null;

    if (originalException.sentryMessage) {
      // when errors have a dedicated sentryMessage property, send that rather than the default "message" property
      event.exception.values[0].value = originalException.sentryMessage;
    }
  } catch (e) {
    // do nothing
  }

  // enrich with more context
  addTranslationDataToEventContext(event);

  if (env === 'local') {
    console.log('Sentry Event:', event);
    return null;
  }
  return event;
}

export default function initializeSentry() {
  Sentry.init({
    dsn: 'https://4acc2d0cb9ba4af586739550b525ea43@sentry.io/1875299',
    integrations: [
      // eslint-disable-next-line new-cap
      Sentry.browserTracingIntegration(),
      // https://docs.sentry.io/platforms/javascript/session-replay/
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
    release: process.env.REACT_APP_GIT_HASH,
    environment: `${env}`,
    tracesSampleRate: isProduction ? 1.0 : 0.1,
    replaySessionSampleRate: 0, // we have a limit on session replays that should be reserved for errors only
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Load failed', // Azure maps failing to fetch
      'Analytics: Failed to fetch',
      'AxiosError: Request failed with status code 401',
    ],
    beforeBreadcrumb,
    beforeSend,
  });
  Sentry.setTag('client', `${client}`);
  Sentry.setTag('mode', isEmbedded() ? 'embedded' : 'standalone');
  Sentry.setContext(BROWSER_SETTINGS, {
    isWebGlDisabled: !detectWebGLSupport(),
  });
}
